import React from 'react';
import { Zap, Lightbulb, Cable, Box, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const ElectricalInfrastructure = () => {
  const { language } = useLanguage();

  const products = [
    {
      icon: Lightbulb,
      title: { fr: 'Éclairage Public', en: 'Public Lighting' },
      description: { fr: 'Poteaux et lampadaires pour éclairage urbain', en: 'Poles and street lights for urban lighting' },
      image: 'https://images.unsplash.com/photo-1532007271951-c487760934ae',
      specs: [
        { fr: 'Poteaux béton et métal', en: 'Concrete and metal poles' },
        { fr: 'Lampadaires solaires et conventionnels', en: 'Solar and conventional street lights' },
        { fr: 'Éclairage LED haute efficacité', en: 'High efficiency LED lighting' },
        { fr: 'Installation et raccordement', en: 'Installation and connection' },
        { fr: 'Maintenance programmée', en: 'Scheduled maintenance' }
      ]
    },
    {
      icon: Zap,
      title: { fr: 'Groupes Électrogènes', en: 'Generators' },
      description: { fr: 'Toutes puissances pour alimentation de secours', en: 'All powers for backup power' },
      image: 'https://images.unsplash.com/photo-1681263576084-e054b2d89903',
      specs: [
        { fr: 'Puissance 10kVA à 2000kVA', en: 'Power 10kVA to 2000kVA' },
        { fr: 'Diesel et gaz naturel', en: 'Diesel and natural gas' },
        { fr: 'Démarrage automatique ATS', en: 'Automatic ATS start' },
        { fr: 'Maintenance préventive incluse', en: 'Preventive maintenance included' },
        { fr: 'Pièces détachées disponibles', en: 'Spare parts available' }
      ]
    },
    {
      icon: Cable,
      title: { fr: 'Câbles HT/MT', en: 'HV/MV Cables' },
      description: { fr: 'Câbles haute et moyenne tension pour distribution', en: 'High and medium voltage cables for distribution' },
      image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e',
      specs: [
        { fr: 'Câbles HT jusqu\'à 150kV', en: 'HV cables up to 150kV' },
        { fr: 'Câbles MT 6kV à 36kV', en: 'MV cables 6kV to 36kV' },
        { fr: 'Coffrets et accessoires', en: 'Boxes and accessories' },
        { fr: 'Pose et raccordement', en: 'Installation and connection' },
        { fr: 'Tests et mise en service', en: 'Testing and commissioning' }
      ]
    },
    {
      icon: Box,
      title: { fr: 'Transformateurs', en: 'Transformers' },
      description: { fr: 'Transformateurs de distribution électrique', en: 'Electrical distribution transformers' },
      image: 'https://images.unsplash.com/photo-1606904825846-647eb07f5be2',
      specs: [
        { fr: 'Transformateurs 50kVA à 2000kVA', en: 'Transformers 50kVA to 2000kVA' },
        { fr: 'Huile et sec (résine)', en: 'Oil and dry (resin)' },
        { fr: 'Montage sur poteau ou poste', en: 'Pole or station mounting' },
        { fr: 'Équipements de protection', en: 'Protection equipment' },
        { fr: 'Maintenance et diagnostics', en: 'Maintenance and diagnostics' }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-32 overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1621905251189-08b45d6a269e)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Zap className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Infrastructures Électriques' : 'Electrical Infrastructure'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr' ? 'Équipements électriques professionnels pour distribution et éclairage' : 'Professional electrical equipment for distribution and lighting'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Équipements' : 'Our Equipment'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {products.map((product, index) => {
              const Icon = product.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img src={product.image} alt={product.title[language]} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{product.title[language]}</h3>
                    <p className="text-gray-600 mb-6">{product.description[language]}</p>
                    <ul className="space-y-3">
                      {product.specs.map((spec, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{spec[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Projet d\'Infrastructure ?' : 'Infrastructure Project?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ? 'Nos ingénieurs vous accompagnent dans votre projet électrique' : 'Our engineers support you in your electrical project'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Demander un Devis' : 'Request a Quote'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ElectricalInfrastructure;
